import { useCallback, useEffect, useRef, useState } from "react";

import type { ChatAttachment, PolyfutureApiClient } from "./api.js";
import {
  admitFiles,
  fileExtension,
  formatAttachmentSize,
  type ComposerAttachmentState,
} from "./attachment-rules.js";

export interface ComposerAttachment {
  readonly key: string;
  readonly file: File;
  readonly mediaType: string;
  readonly photo: boolean;
  readonly state: ComposerAttachmentState;
  readonly attachmentId?: string;
  /** Only set once the browser holds a real object URL for the photo. */
  readonly previewUrl?: string;
  readonly problem?: string;
}

const POLL_INTERVAL_MS = 1_000;
const POLL_LIMIT = 120;

/**
 * ADR-092. The files attached to the next question: each is uploaded to quarantine, then polled
 * until the server has scanned and read it. Only ready files can be sent.
 */
export function useComposerAttachments(client: PolyfutureApiClient) {
  const [items, setItems] = useState<readonly ComposerAttachment[]>([]);
  const [errors, setErrors] = useState<readonly string[]>([]);
  const alive = useRef(true);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    // Set on every mount: development mounts twice, and the first cleanup must not stick.
    alive.current = true;
    return () => {
      alive.current = false;
      for (const item of itemsRef.current) {
        if (item.previewUrl !== undefined) URL.revokeObjectURL(item.previewUrl);
      }
    };
  }, []);

  const update = useCallback((key: string, patch: Partial<ComposerAttachment>) => {
    if (!alive.current) return;
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  }, []);

  const upload = useCallback(
    async (key: string, file: File, mediaType: string) => {
      update(key, { state: "uploading" });
      let attachment: ChatAttachment;
      try {
        attachment = await client.uploadChatAttachment(file, mediaType);
      } catch {
        update(key, { state: "failed", problem: "Upload failed" });
        return;
      }
      update(key, { state: "reading", attachmentId: attachment.id });
      for (let poll = 0; poll < POLL_LIMIT && alive.current; poll += 1) {
        if (attachment.status === "ready") {
          update(key, { state: "ready" });
          return;
        }
        if (attachment.status === "rejected" || attachment.status === "failed") {
          update(key, {
            state: "failed",
            problem: attachment.reason ?? "The file could not be read.",
          });
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        try {
          attachment = await client.getChatAttachment(attachment.id);
        } catch {
          // A missed poll is retried on the next interval.
        }
      }
      update(key, { state: "failed", problem: "Reading the file took too long." });
    },
    [client, update],
  );

  const add = useCallback(
    (files: readonly File[]) => {
      const { admitted, errors: refused } = admitFiles(itemsRef.current.length, files);
      setErrors(refused);
      const added = admitted.map(({ file, mediaType, photo }) => ({
        key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        mediaType,
        photo,
        state: "uploading" as const,
        ...(photo ? { previewUrl: URL.createObjectURL(file) } : {}),
      }));
      if (added.length === 0) return;
      setItems((current) => [...current, ...added]);
      for (const item of added) void upload(item.key, item.file, item.mediaType);
    },
    [upload],
  );

  const remove = useCallback((key: string) => {
    setItems((current) => {
      const removed = current.find((item) => item.key === key);
      if (removed?.previewUrl !== undefined) URL.revokeObjectURL(removed.previewUrl);
      return current.filter((item) => item.key !== key);
    });
    setErrors([]);
  }, []);

  const retry = useCallback(
    (key: string) => {
      const item = itemsRef.current.find((candidate) => candidate.key === key);
      if (item !== undefined) void upload(item.key, item.file, item.mediaType);
    },
    [upload],
  );

  /** Hands the sent files to the question and empties the composer, keeping their previews. */
  const take = useCallback((): readonly ComposerAttachment[] => {
    const taken = itemsRef.current;
    setItems([]);
    setErrors([]);
    return taken;
  }, []);

  return { items, errors, add, remove, retry, take };
}

export function AttachmentChips({
  items,
  onRemove,
  onRetry,
}: {
  readonly items: readonly ComposerAttachment[];
  readonly onRemove: (key: string) => void;
  readonly onRetry: (key: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="composer-attachments" aria-label="Attached files">
      {items.map((item) => (
        <li
          className={
            item.state === "failed"
              ? "composer-attachment composer-attachment-failed"
              : "composer-attachment"
          }
          key={item.key}
        >
          {item.previewUrl === undefined ? (
            <span className="composer-attachment-tile" aria-hidden="true">
              {fileExtension(item.file.name)}
            </span>
          ) : (
            <img className="composer-attachment-thumb" src={item.previewUrl} alt="" />
          )}
          <span className="composer-attachment-text">
            <strong title={item.file.name}>{item.file.name}</strong>
            <small role={item.state === "failed" ? "alert" : undefined}>
              {item.state === "uploading" ? (
                <>
                  <span className="composer-attachment-spinner" aria-hidden="true" /> Uploading…
                </>
              ) : item.state === "reading" ? (
                <>
                  <span className="composer-attachment-spinner" aria-hidden="true" /> Checking and
                  reading…
                </>
              ) : item.state === "ready" ? (
                `${formatAttachmentSize(item.file.size)} · ready`
              ) : (
                (item.problem ?? "Upload failed")
              )}
            </small>
          </span>
          {item.state === "failed" && item.problem === "Upload failed" ? (
            <button
              type="button"
              className="composer-attachment-retry"
              onClick={() => onRetry(item.key)}
            >
              Retry
            </button>
          ) : null}
          <button
            type="button"
            className="composer-attachment-remove"
            aria-label={`Remove ${item.file.name}`}
            onClick={() => onRemove(item.key)}
          >
            ✕
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Chips above a sent question: names only, since a sent photo's preview is not kept. */
export function SentAttachments({
  attachments,
}: {
  readonly attachments: readonly { readonly name: string }[];
}) {
  if (attachments.length === 0) return null;
  return (
    <ul className="sent-attachments" aria-label="Attached to this question">
      {attachments.map((attachment, index) => (
        <li key={`${attachment.name}-${index}`}>
          <span className="composer-attachment-tile" aria-hidden="true">
            {fileExtension(attachment.name)}
          </span>
          <span>{attachment.name}</span>
        </li>
      ))}
    </ul>
  );
}
